import { useState } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Alert, 
  InputAdornment, 
  IconButton,
  Paper,
  Fade,
  Slide
} from '@mui/material';
import { 
  Email, 
  Lock, 
  Visibility, 
  VisibilityOff, 
  PersonAdd,
  FitnessCenter,
  Person
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { api, authenticatedFetch } from '../utils/api';

function Register({ onRegister, onSwitchToLogin }) {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { darkMode } = useTheme();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(api.register, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, email, password }),
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        onRegister(data.user, data.token);
      } else {
        setError(data.error || 'Registration failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fade in timeout={800}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        minHeight: '100vh',
        justifyContent: 'center',
        px: 3,
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Animated Background Elements */}
        <Box sx={{
          position: 'absolute',
          top: '10%',
          left: '10%',
          width: 200,
          height: 200,
          background: darkMode 
            ? 'radial-gradient(circle, rgba(129, 140, 248, 0.1) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%)',
          borderRadius: '50%',
          animation: 'float 6s ease-in-out infinite',
          '@keyframes float': {
            '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
            '50%': { transform: 'translateY(-20px) rotate(180deg)' }
          }
        }} />
        
        <Box sx={{
          position: 'absolute',
          bottom: '20%',
          right: '15%',
          width: 150,
          height: 150,
          background: darkMode 
            ? 'radial-gradient(circle, rgba(52, 211, 153, 0.1) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(6, 214, 160, 0.15) 0%, transparent 70%)',
          borderRadius: '50%',
          animation: 'float 8s ease-in-out infinite reverse',
        }} />

        {/* Additional Light Mode Elements */}
        {!darkMode && (
          <>
            <Box sx={{
              position: 'absolute',
              top: '30%',
              right: '20%',
              width: 100,
              height: 100,
              background: 'radial-gradient(circle, rgba(245, 158, 11, 0.1) 0%, transparent 70%)',
              borderRadius: '50%',
              animation: 'float 10s ease-in-out infinite',
            }} />
            
            <Box sx={{
              position: 'absolute',
              bottom: '40%',
              left: '20%',
              width: 80,
              height: 80,
              background: 'radial-gradient(circle, rgba(168, 85, 247, 0.1) 0%, transparent 70%)',
              borderRadius: '50%',
              animation: 'float 12s ease-in-out infinite reverse',
            }} />
          </>
        )}

        {/* Main Register Card */}
        <Slide direction="up" in timeout={1000}>
          <Paper elevation={8} sx={{
            width: '100%',
            maxWidth: 420,
            p: 4,
            borderRadius: 3,
            background: darkMode 
              ? '#1e293b'
              : '#ffffff',
            border: darkMode 
              ? '1px solid #334155'
              : '1px solid #e2e8f0',
            boxShadow: darkMode
              ? '0 10px 25px rgba(0, 0, 0, 0.3)'
              : '0 10px 25px rgba(0, 0, 0, 0.1)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                mb: 2 
              }}>
                <FitnessCenter sx={{ 
                  fontSize: 40, 
                  color: 'primary.main',
                  mr: 1,
                  animation: 'pulse 2s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%, 100%': { transform: 'scale(1)' },
                    '50%': { transform: 'scale(1.1)' }
                  }
                }} />
                <Typography variant="h4" sx={{ 
                  fontWeight: 800,
                  background: darkMode 
                    ? 'linear-gradient(45deg, #818cf8, #34d399)'
                    : 'linear-gradient(45deg, #6366f1, #06d6a0)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  FitCoach AI
                </Typography>
              </Box>
              <Typography variant="h5" sx={{ 
                fontWeight: 600,
                color: 'text.primary',
                mb: 1
              }}>
                Join the Journey
              </Typography>
              <Typography variant="body1" sx={{ 
                color: 'text.secondary',
                opacity: 0.8
              }}>
                Create your account to start your wellness transformation
              </Typography>
            </Box>

            {/* Error Alert */}
            {error && (
              <Fade in>
                <Alert 
                  severity="error" 
                  sx={{ 
                    mb: 3,
                    borderRadius: 2,
                    background: darkMode 
                      ? 'rgba(248, 113, 113, 0.1)'
                      : 'rgba(239, 68, 68, 0.1)',
                    border: `1px solid ${darkMode ? 'rgba(248, 113, 113, 0.2)' : 'rgba(239, 68, 68, 0.2)'}`
                  }}
                >
                  {error}
                </Alert>
              </Fade>
            )}

            {/* Register Form */}
            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                fullWidth
                label="Username"
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
                inputProps={{ minLength: 3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    background: darkMode 
                      ? '#334155'
                      : '#f8fafc',
                    border: darkMode 
                      ? '1px solid #475569'
                      : '1px solid #cbd5e1',
                    '&:hover': {
                      background: darkMode 
                        ? '#475569'
                        : '#f1f5f9',
                      border: darkMode 
                        ? '1px solid #64748b'
                        : '1px solid #94a3b8',
                    },
                    '&.Mui-focused': {
                      background: darkMode 
                        ? '#475569'
                        : '#ffffff',
                      border: darkMode 
                        ? '2px solid #818cf8'
                        : '2px solid #6366f1',
                    }
                  }
                }}
              />

              <TextField
                fullWidth
                label="Email Address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    background: darkMode 
                      ? '#334155'
                      : '#f8fafc',
                    border: darkMode 
                      ? '1px solid #475569'
                      : '1px solid #cbd5e1',
                    '&:hover': {
                      background: darkMode 
                        ? '#475569'
                        : '#f1f5f9',
                      border: darkMode 
                        ? '1px solid #64748b'
                        : '1px solid #94a3b8',
                    },
                    '&.Mui-focused': {
                      background: darkMode 
                        ? '#475569'
                        : '#ffffff',
                      border: darkMode 
                        ? '2px solid #818cf8'
                        : '2px solid #6366f1',
                    }
                  }
                }}
              />

              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
                inputProps={{ minLength: 6 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: 'text.secondary' }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    background: darkMode 
                      ? '#334155'
                      : '#f8fafc',
                    border: darkMode 
                      ? '1px solid #475569'
                      : '1px solid #cbd5e1',
                    '&:hover': {
                      background: darkMode 
                        ? '#475569'
                        : '#f1f5f9',
                      border: darkMode 
                        ? '1px solid #64748b'
                        : '1px solid #94a3b8',
                    },
                    '&.Mui-focused': {
                      background: darkMode 
                        ? '#475569'
                        : '#ffffff',
                      border: darkMode 
                        ? '2px solid #818cf8'
                        : '2px solid #6366f1',
                    }
                  }
                }}
              />

              <TextField
                fullWidth
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                        sx={{ color: 'text.secondary' }}
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    background: darkMode 
                      ? '#334155'
                      : '#f8fafc',
                    border: darkMode 
                      ? '1px solid #475569'
                      : '1px solid #cbd5e1',
                    '&:hover': {
                      background: darkMode 
                        ? '#475569'
                        : '#f1f5f9',
                      border: darkMode 
                        ? '1px solid #64748b'
                        : '1px solid #94a3b8',
                    },
                    '&.Mui-focused': {
                      background: darkMode 
                        ? '#475569'
                        : '#ffffff',
                      border: darkMode 
                        ? '2px solid #818cf8'
                        : '2px solid #6366f1',
                    }
                  }
                }}
              />

              <Button
          type="submit" 
                fullWidth
                variant="contained"
          disabled={loading}
                startIcon={loading ? null : <PersonAdd />}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  background: darkMode 
                    ? 'linear-gradient(45deg, #34d399, #818cf8)'
                    : 'linear-gradient(45deg, #06d6a0, #6366f1)',
                  boxShadow: darkMode
                    ? '0 8px 32px rgba(52, 211, 153, 0.3)'
                    : '0 8px 32px rgba(6, 214, 160, 0.3)',
                  '&:hover': {
                    background: darkMode 
                      ? 'linear-gradient(45deg, #10b981, #6366f1)'
                      : 'linear-gradient(45deg, #059669, #4f46e5)',
                    boxShadow: darkMode
                      ? '0 12px 40px rgba(52, 211, 153, 0.4)'
                      : '0 12px 40px rgba(6, 214, 160, 0.4)',
                    transform: 'translateY(-2px)',
                  },
                  '&:active': {
                    transform: 'translateY(0px)',
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </Button>

              {/* Login Link */}
              <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Typography variant="body2" sx={{ 
                  color: 'text.secondary',
                  mb: 1
                }}>
                  Already have an account?
                </Typography>
                <Typography 
                  component="span" 
                  sx={{ 
                    color: darkMode ? '#34d399' : '#06d6a0',
                    fontWeight: 600,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    '&:hover': {
                      color: darkMode ? '#10b981' : '#059669',
                    },
                    transition: 'color 0.2s ease'
                  }}
                  onClick={onSwitchToLogin}
                >
                  Login here
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Slide>
      </Box>
    </Fade>
  );
}

export default Register; 